import { Text } from 'preact-i18n';
import cx from 'classnames';
import get from 'get-value';

import ConfigSchemaForm from './ConfigSchemaForm';
import DocsLink from '../components/DocsLink';
import ActionsCard from './ActionsCard';
import LinkAccountCard from './LinkAccountCard';
import ContactProfileCard from './ContactProfileCard';
import WebhooksCard from './WebhooksCard';
import HardwareCard from './HardwareCard';
import { getAssignedPortsByName, getRequestedHardwareClasses } from '../utils';
import { RequestStatus } from '../../../../../utils/consts';
import { USER_ROLE } from '../../../../../../../server/utils/constants';

const ConfigTab = props => {
  const { integration, loadStatus, user } = props;
  // the shared configuration of the integration (settings, webhooks,
  // actions, hardware) is administration; a non-admin user only gets the
  // per-user blocks, to link their own account on a communication
  // integration — exactly like on the native Telegram service
  const isAdmin = get(user, 'role') === USER_ROLE.ADMIN;
  const schema = get(integration, 'manifest.config_schema') || [];
  const actions = get(integration, 'manifest.actions') || [];
  const manifestWebhooks = get(integration, 'manifest.webhooks') || [];
  const transports = get(integration, 'manifest.transports') || [];
  const hasDualTransports = transports.includes('local') && transports.includes('cloud');
  const language = (user && user.language) || 'en';
  const isCommunication = get(integration, 'manifest.type') === 'communication';
  // chat channels (receive true, the default) link by code; send-only
  // notification channels expose the per-user "My account" block instead
  const isReceivingChannel = isCommunication && get(integration, 'manifest.messaging.receive') !== false;
  const contactSchema = get(integration, 'manifest.contact_schema') || [];
  const requestedClasses = getRequestedHardwareClasses(get(integration, 'manifest.containers') || []);
  // host ports assigned to the manifest-named declared ports, for the
  // {{port:<name>}} placeholders of the section texts
  const placeholderPorts = getAssignedPortsByName(integration);
  // permanent link to the mandatory re-hosted docs (store installs): it
  // is while configuring that the user needs them most (create the
  // vendor developer account, get credentials...)
  const docs = get(integration, 'docs') || {};
  const docsUrl = docs[language] || docs.en;

  return (
    <div>
      {!isAdmin && loadStatus === RequestStatus.Error && (
        <div class="alert alert-danger">
          <Text id="integration.externalIntegration.config.loadError" />
        </div>
      )}

      {isAdmin && (
        <div class="card">
          <div class="card-header">
            <h1 class="card-title">
              <Text id="integration.externalIntegration.config.title" />
            </h1>
            {docsUrl && (
              <div class="card-options">
                <DocsLink
                  docsUrl={docsUrl}
                  storeSlug={integration.store_slug}
                  lang={language}
                  httpClient={props.httpClient}
                  labelId="integration.externalIntegration.config.docsLink"
                  linkClass="btn btn-outline-secondary btn-sm"
                />
              </div>
            )}
          </div>
          <div class="card-body">
            {loadStatus === RequestStatus.Error && (
              <div class="alert alert-danger">
                <Text id="integration.externalIntegration.config.loadError" />
              </div>
            )}
            <div
              class={cx('dimmer', {
                active: loadStatus === RequestStatus.Getting
              })}
            >
              <div class="loader" />
              <div class="dimmer-content">
                {hasDualTransports && loadStatus === RequestStatus.Success && (
                  <div class="form-group">
                    <label class="custom-switch">
                      <input
                        type="checkbox"
                        class="custom-switch-input"
                        checked={get(props, 'configValues.GLADYS_PREFER_LOCAL') !== false}
                        onClick={props.togglePreferLocal}
                        disabled={props.preferLocalStatus === RequestStatus.Getting}
                      />
                      <span class="custom-switch-indicator" />
                      <span class="custom-switch-description">
                        <Text id="integration.externalIntegration.transport.preferLocalLabel" />
                      </span>
                    </label>
                    <small class="form-text text-muted">
                      <Text id="integration.externalIntegration.transport.preferLocalDescription" />
                    </small>
                    {props.preferLocalStatus === RequestStatus.Error && (
                      <div class="alert alert-danger mt-2">
                        <Text id="integration.externalIntegration.config.saveError" />
                      </div>
                    )}
                  </div>
                )}
                {loadStatus === RequestStatus.Success && schema.length === 0 && (
                  <div class="text-muted">
                    <Text id="integration.externalIntegration.config.noConfig" />
                  </div>
                )}
                {schema.length > 0 && (
                  <ConfigSchemaForm
                    schema={schema}
                    language={language}
                    values={props.configValues || {}}
                    configuredSecrets={props.configuredSecrets || []}
                    touchedSecrets={props.touchedSecrets || {}}
                    saveConfigStatus={props.saveConfigStatus}
                    updateConfigValue={props.updateConfigValue}
                    saveConfig={props.saveConfig}
                    connectionStatus={get(integration, 'connection_status')}
                    oauthStatus={props.oauthStatus}
                    oauthInvalidState={props.oauthInvalidState}
                    oauthUseInstanceRedirect={props.oauthUseInstanceRedirect}
                    toggleOAuthUseInstanceRedirect={props.toggleOAuthUseInstanceRedirect}
                    connectOAuth={props.connectOAuth}
                    selector={props.selector || get(integration, 'selector')}
                    dynamicOptions={props.dynamicOptions}
                    placeholderPorts={placeholderPorts}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isReceivingChannel && (
        <LinkAccountCard
          contact={props.contact}
          linkCode={props.linkCode}
          linkStatus={props.linkStatus}
          onGenerateCode={props.generateLinkCode}
          onUnlink={props.unlinkContact}
        />
      )}

      {integration && contactSchema.length > 0 && (
        <ContactProfileCard
          contactSchema={contactSchema}
          language={language}
          profile={props.contactProfile}
          values={props.contactProfileValues}
          configuredSecrets={get(props, 'contactProfile.configured_secrets')}
          touchedSecrets={props.contactProfileTouchedSecrets}
          profileStatus={props.contactProfileStatus}
          updateValue={props.updateContactProfileValue}
          onSave={props.saveContactProfile}
          onClear={props.clearContactProfile}
        />
      )}

      {isAdmin && integration && manifestWebhooks.length > 0 && (
        <WebhooksCard
          manifestWebhooks={manifestWebhooks}
          webhooks={get(integration, 'webhooks')}
          gatewayStatus={props.gatewayStatus}
          keyConfigured={(props.configuredSecrets || []).includes('GLADYS_OPEN_API_KEY')}
          openApiKeyValue={props.openApiKeyValue}
          openApiKeyStatus={props.openApiKeyStatus}
          language={language}
          onOpenApiKeyInput={props.updateOpenApiKey}
          onSaveOpenApiKey={props.saveOpenApiKey}
        />
      )}

      {isAdmin && integration && actions.length > 0 && (
        <ActionsCard
          actions={actions}
          language={language}
          actionStates={props.actionStates || {}}
          actionFieldValues={props.actionFieldValues || {}}
          updateActionFieldValue={props.updateActionFieldValue}
          runAction={props.runAction}
          dynamicOptions={props.dynamicOptions}
          placeholderPorts={placeholderPorts}
        />
      )}

      {isAdmin && integration && requestedClasses.length > 0 && (
        <HardwareCard
          requestedClasses={requestedClasses}
          detectedClasses={props.detectedClasses || {}}
          grantedDevices={props.grantedDevices || []}
          hardwareStatus={props.hardwareStatus}
          onToggle={props.toggleHardwareClass}
          onSave={props.saveHardware}
        />
      )}
    </div>
  );
};

export default ConfigTab;
